'use client'

import { useSolanaUsdcBalance } from '@/hooks/kalshi/useSolanaBalance'
import usePolygonBalances from '@/hooks/polymarket/usePolygonBalances'
import {
	CHAIN_CONFIG,
	type RelayQuoteResponse,
	type RelayStep,
	getRelayQuote,
	getRelayStatus,
} from '@/lib/api/relay'
import { executeWithRelayerFallback } from '@/lib/relayer-fallback'
import { ensureGasBalance } from '@/lib/solana/ensureGasBalance'
import { useWallet } from '@/providers/WalletContext'
import { deriveSafeAddress } from '@/utils/safe'
import { useSolanaTransaction } from '@account-kit/react'
import {
	OperationType,
	type RelayClient,
	type SafeTransaction,
} from '@polymarket/builder-relayer-client'
import { PublicKey, TransactionMessage, VersionedTransaction } from '@solana/web3.js'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

export type TransferDirection = 'toPolymarket' | 'toKalshi'

export type TransferStatus =
	| 'idle'
	| 'loading_quote'
	| 'quote_ready'
	| 'executing'
	| 'success'
	| 'error'

export interface TransferQuote {
	steps: RelayStep[]
	totalFeeUsd: string
	estimatedOutput: string
	timeEstimate: number
	inputAmount: string
	direction: TransferDirection
	raw: RelayQuoteResponse
}

export interface TransferParams {
	amount: string
	direction: TransferDirection
	useMaxBalance?: boolean
	exactOutput?: boolean // Request exact output amount (for trade prompts)
}

function relayTxToSafeTx(data: {
	to?: string
	data?: string
	value?: string
}): SafeTransaction | null {
	if (!(data.to && data.data)) return null
	return {
		to: data.to,
		data: data.data,
		value: data.value || '0',
		operation: OperationType.Call,
	}
}

export function useInternalTransfer() {
	const [status, setStatus] = useState<TransferStatus>('idle')
	const [quote, setQuote] = useState<TransferQuote | null>(null)
	const [currentStepIndex, setCurrentStepIndex] = useState(0)
	const [error, setError] = useState<string | null>(null)
	const [txHash, setTxHash] = useState<string | null>(null)

	const queryClient = useQueryClient()
	const { eoaAddress, solanaAddress, walletClient } = useWallet()
	const { connection: solanaConnection, signer: solanaSigner } = useSolanaTransaction()
	const safeAddress = eoaAddress ? deriveSafeAddress(eoaAddress) : undefined

	// Balances
	const { rawUsdcBalance: polygonRawBalance } = usePolygonBalances(safeAddress)
	const { data: solanaBalanceData } = useSolanaUsdcBalance()

	const polymarketBalance = useMemo(() => {
		if (!polygonRawBalance) return 0
		return Number(polygonRawBalance) / 1_000_000
	}, [polygonRawBalance])

	const kalshiBalance = useMemo(() => {
		if (!solanaBalanceData) return 0
		return solanaBalanceData.balance / 1_000_000
	}, [solanaBalanceData])

	// Fetch quote for internal transfer
	const fetchQuote = useCallback(
		async (params: TransferParams) => {
			if (!(safeAddress && solanaAddress)) {
				throw new Error('Wallet not connected')
			}

			setStatus('loading_quote')
			setError(null)
			setQuote(null)

			try {
				const isToPolymarket = params.direction === 'toPolymarket'

				// Source balance check (skip for exact output - API will calculate input)
				const sourceRaw = isToPolymarket
					? solanaBalanceData?.balance
						? BigInt(solanaBalanceData.balance)
						: BigInt(0)
					: polygonRawBalance
						? BigInt(polygonRawBalance)
						: BigInt(0)

				let amountInSmallestUnit: string
				let amountNum: number

				if (params.useMaxBalance) {
					amountInSmallestUnit = sourceRaw.toString()
					amountNum = Number(sourceRaw) / 1_000_000
				} else {
					amountNum = Number.parseFloat(params.amount)
					if (Number.isNaN(amountNum) || amountNum <= 0) {
						throw new Error('Invalid amount')
					}
					const requestedRaw = BigInt(Math.floor(amountNum * 1_000_000))
					// Only check source balance for EXACT_INPUT (not for EXACT_OUTPUT)
					if (!params.exactOutput && requestedRaw > sourceRaw) {
						throw new Error('Insufficient balance')
					}
					amountInSmallestUnit = requestedRaw.toString()
				}

				// Configure origin and destination
				const originChainId = isToPolymarket ? CHAIN_CONFIG.solana.id : CHAIN_CONFIG.polygon.id
				const destinationChainId = isToPolymarket ? CHAIN_CONFIG.polygon.id : CHAIN_CONFIG.solana.id
				const originUsdc = isToPolymarket ? CHAIN_CONFIG.solana.usdc : CHAIN_CONFIG.polygon.usdc
				const destinationUsdc = isToPolymarket
					? CHAIN_CONFIG.polygon.usdc
					: CHAIN_CONFIG.solana.usdc
				const user = isToPolymarket ? solanaAddress : safeAddress
				const recipient = isToPolymarket ? safeAddress : solanaAddress

				const response = await getRelayQuote({
					user,
					originChainId,
					destinationChainId,
					originCurrency: originUsdc,
					destinationCurrency: destinationUsdc,
					amount: amountInSmallestUnit,
					recipient,
					tradeType: params.exactOutput ? 'EXACT_OUTPUT' : 'EXACT_INPUT',
				})

				const gasUsd = Number.parseFloat(response.fees.gas?.amountUsd || '0')
				const relayerUsd = Number.parseFloat(response.fees.relayer?.amountUsd || '0')
				const totalFeeUsd = (gasUsd + relayerUsd).toFixed(2)

				// For EXACT_OUTPUT, use the calculated input amount from the API response
				const inputAmount = params.exactOutput
					? response.details.currencyIn.amountFormatted
					: amountNum.toFixed(2)

				const transferQuote: TransferQuote = {
					steps: response.steps,
					totalFeeUsd,
					estimatedOutput: response.details.currencyOut.amountFormatted,
					timeEstimate: response.details.timeEstimate || 5,
					inputAmount,
					direction: params.direction,
					raw: response,
				}

				setQuote(transferQuote)
				setStatus('quote_ready')
				return transferQuote
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to get quote'
				setError(message)
				setStatus('error')
				throw err
			}
		},
		[safeAddress, solanaAddress, polygonRawBalance, solanaBalanceData]
	)

	// Execute transfer
	const execute = useCallback(
		async (relayClient: RelayClient | null) => {
			if (!quote) {
				setError('No quote available')
				setStatus('error')
				return
			}

			setStatus('executing')
			setCurrentStepIndex(0)
			setTxHash(null)

			try {
				const isToPolymarket = quote.direction === 'toPolymarket'
				let hash: string | null = null

				if (isToPolymarket) {
					// Solana → Polymarket: Execute Solana transaction
					if (!(solanaConnection && solanaSigner && solanaAddress)) {
						throw new Error('Solana wallet not available')
					}

					// Ensure user has SOL for gas (auto-faucet if needed)
					await ensureGasBalance(solanaConnection, solanaAddress)

					for (const step of quote.steps) {
						for (const item of step.items) {
							if (item.data?.instructions) {
								const { blockhash } = await solanaConnection.getLatestBlockhash()

								const instructions = (
									item.data.instructions as Array<{
										keys: Array<{ pubkey: string; isSigner: boolean; isWritable: boolean }>
										programId: string
										data: string
									}>
								).map(ix => ({
									keys: ix.keys.map(k => ({
										pubkey: new PublicKey(k.pubkey),
										isSigner: k.isSigner,
										isWritable: k.isWritable,
									})),
									programId: new PublicKey(ix.programId),
									data: Buffer.from(ix.data, 'hex'),
								}))

								const message = new TransactionMessage({
									payerKey: new PublicKey(solanaAddress),
									recentBlockhash: blockhash,
									instructions,
								}).compileToV0Message()

								const tx = new VersionedTransaction(message)
								const signedTx = await solanaSigner.addSignature(tx)

								let signature: string
								try {
									signature = await solanaConnection.sendRawTransaction(signedTx.serialize(), {
										skipPreflight: false,
										preflightCommitment: 'confirmed',
									})
								} catch (err) {
									const errorMessage = err instanceof Error ? err.message : String(err)
									if (
										errorMessage.includes(
											'Attempt to debit an account but found no record of a prior credit'
										)
									) {
										throw new Error(
											"We're having issues landing the transaction on Solana. The chain might be busy, please retry."
										)
									}
									throw err
								}
								for (let i = 0; i < 30; i++) {
									const txStatus = await solanaConnection.getSignatureStatus(signature)
									if (
										txStatus.value?.confirmationStatus === 'confirmed' ||
										txStatus.value?.confirmationStatus === 'finalized'
									) {
										break
									}
									if (txStatus.value?.err) {
										throw new Error(`Transaction failed: ${JSON.stringify(txStatus.value.err)}`)
									}
									await new Promise(r => setTimeout(r, 1000))
								}

								hash = signature
							}
						}
					}
				} else {
					// Polymarket → Kalshi: Execute via RelayClient
					if (!relayClient) {
						throw new Error('Trading session not initialized')
					}

					const polygonTxs: SafeTransaction[] = []
					for (const step of quote.steps) {
						for (const item of step.items) {
							if (item.data?.chainId === CHAIN_CONFIG.polygon.id) {
								const safeTx = relayTxToSafeTx(item.data)
								if (safeTx) polygonTxs.push(safeTx)
							}
						}
					}

					if (polygonTxs.length > 0) {
						const response = await executeWithRelayerFallback(
							relayClient,
							walletClient,
							polygonTxs,
							'Transfer USDC to Kalshi'
						)
						const result = (await response.wait()) as { txHash?: string } | null
						hash = result?.txHash || null
					}
				}

				// Poll for relay completion
				for (const step of quote.steps) {
					if (step.requestId) {
						let attempts = 0
						while (attempts < 60) {
							try {
								const statusResult = await getRelayStatus(step.requestId)
								if (statusResult.status === 'success') {
									break
								}
								if (statusResult.status === 'failure') {
									console.warn('[Transfer] Relay failed:', statusResult.data?.failReason)
									break
								}
							} catch {
								// Status endpoint might not be available yet
							}
							await new Promise(r => setTimeout(r, 2000))
							attempts++
						}
					}
				}

				setTxHash(hash)
				setStatus('success')

				// Invalidate balance queries
				queryClient.invalidateQueries({ queryKey: ['usdcBalance'] })
				queryClient.invalidateQueries({ queryKey: ['solanaBalance'] })
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Transfer failed'
				setError(message)
				setStatus('error')
				console.error('[Transfer] Error:', err)
			}
		},
		[quote, solanaConnection, solanaSigner, solanaAddress, queryClient, walletClient]
	)

	const reset = useCallback(() => {
		setStatus('idle')
		setQuote(null)
		setCurrentStepIndex(0)
		setError(null)
		setTxHash(null)
	}, [])

	return {
		// Balances
		polymarketBalance,
		kalshiBalance,
		// Quote
		quote,
		fetchQuote,
		// Execution
		execute,
		// Status
		status,
		currentStepIndex,
		error,
		txHash,
		// Actions
		reset,
	}
}
